import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, Check, Truck, Shield, RotateCcw, Heart, ShoppingCart, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Waves, Anchor, Sparkles, Play, X, Info, Droplet, Ruler, Flag, Snowflake, ExternalLink } from 'lucide-react';
import { getProduct, getProducts } from '../api/api';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import { SEO, productSchema, breadcrumbSchema } from '../components/SEO';
import { ZoomImage } from '../components/ZoomImage';
import { imgUrl, productImgUrl } from '../utils/imgUrl';
import { DepthGuide } from '../components/DepthGuide';
import { SaleCountdown } from '../components/SaleCountdown';
import { useSale, salePrice, saleAppliesTo, salePercent } from '../utils/sale';
import { STANDARD_COLORS } from '../utils/shipping';
import ProductReviews from '../components/ProductReviews';
import { ProductVideos } from '../components/ProductVideos';

const VIDEO_SLIDE = '__video__';

const BALLAST_PRODUCT_NAMES = {
  'tenjam-shayz-lounge-chair-set': { display: 'Shayz\u00AE Lounger', short: 'Shayz\u00AE Lounger' },
  'tenjam-freelo-lounge-chair': { display: 'Freelo Chair\u00AE', short: 'Freelo Chair\u00AE' },
  'tenjam-laylo-lounge-set': { display: 'Laylo\u00AE Lounger', short: 'Laylo\u00AE Lounger' },
  'tenjam-woosah-adirondack-chair': { display: 'Woosah Chair\u00AE', short: 'Woosah Chair\u00AE' },
  'tenjam-moon-chair-set': { display: 'Moon Chair\u00AE', short: 'Moon Chair\u00AE' },
  'tenjam-mibster-chair-set': { display: 'Mibster Chair\u00AE', short: 'Mibster Chair\u00AE' },
};

const ProductDetail = () => {
  const { slug, category } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [displayImage, setDisplayImage] = useState('');
  const [slideIndex, setSlideIndex] = useState(0);
  const [manualPick, setManualPick] = useState(false);
  const touchStartX = useRef(null);
  const goToSlide = (dir) => {
    const n = thumbnails.length;
    if (!n || n <= 1) return;
    const ni = (slideIndex + dir + n) % n;
    setSlideIndex(ni);
    setDisplayImage(thumbnails[ni]);
  };
  const [selectedCustomizations, setSelectedCustomizations] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [reviewStats, setReviewStats] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeInfo, setActiveInfo] = useState(null);
  const [validationError, setValidationError] = useState('');
  // Required acknowledgement when buyer selects an "With Umbrella Hole" option:
  // they must confirm an umbrella sleeve is installed in the pool floor.
  const [umbrellaSleeveConfirmed, setUmbrellaSleeveConfirmed] = useState(false);

  // True when any customization option for this product ends with the words "Umbrella Hole"
  // AND the buyer has actively chosen the "With Umbrella Hole" variant.
  const requiresUmbrellaSleeve = !!(product?.customizations || []).find(
    (c) => /umbrella hole/i.test(c.name) && /^with /i.test(selectedCustomizations[c.name] || '')
  );

  // Lock body scroll while video modal is open + handle Esc key
  useEffect(() => {
    if (!activeVideo) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setActiveVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [activeVideo]);

  // Lock body scroll while customization info modal is open + Esc to close
  useEffect(() => {
    if (!activeInfo) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setActiveInfo(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [activeInfo]);

  const updateDisplayImage = (color, customizations, prod) => {
    const p = prod || product;
    if (!p?.colorImages) return;
    // Prefer the Soft Touch image when the soft-touch upgrade is selected
    if (Object.values(customizations).includes('With Soft Touch Top')) {
      const stKey = `${color}|With Soft Touch Top`;
      if (p.colorImages[stKey]) {
        setDisplayImage(p.colorImages[stKey]);
        return;
      }
    }
    // Try combo key first: "Color|CustomizationValue"
    for (const [custName, custValue] of Object.entries(customizations)) {
      const comboKey = `${color}|${custValue}`;
      if (p.colorImages[comboKey]) {
        setDisplayImage(p.colorImages[comboKey]);
        return;
      }
    }
    // Fallback to color-only key
    if (p.colorImages[color]) {
      setDisplayImage(p.colorImages[color]);
      return;
    }
    // Fallback to default image
    setDisplayImage(p.image);
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setValidationError('');
    updateDisplayImage(color, selectedCustomizations);
    // Jump off the video slide to the newly selected color's first image.
    const hv = (Array.isArray(product?.videos) && product.videos[0]) || product?.video || null;
    setSlideIndex(hv ? 1 : 0);
    setManualPick(true);
  };

  const handleCustomizationSelect = (name, value) => {
    const updated = { ...selectedCustomizations, [name]: value };
    setSelectedCustomizations(updated);
    setValidationError('');
    // Reset the umbrella-sleeve checkbox if the user changes the umbrella selection — they need to re-acknowledge.
    if (/umbrella hole/i.test(name)) {
      setUmbrellaSleeveConfirmed(false);
    }
    updateDisplayImage(selectedColor, updated);
  };

  const computePriceModifier = () => {
    if (!product?.customizations) return 0;
    let modifier = 0;
    // 1. priceMatrix lookup uses only customizations NOT flagged excludeFromMatrix.
    // If a matrix option is not yet selected, fall back to its first option (e.g. "No Riser")
    // so the price reflects the other selected matrix options immediately.
    const matrixCusts = product.customizations.filter((c) => !c.excludeFromMatrix);
    if (product.priceMatrix && matrixCusts.length > 0) {
      const values = matrixCusts.map((c) => {
        let v = selectedCustomizations[c.name];
        if (v === undefined || v === null || v === '') {
          v = (c.options && c.options.length > 0) ? c.options[0] : v;
        }
        return v;
      });
      const allSet = values.every((v) => v !== undefined && v !== null && v !== '');
      if (allSet) {
        const key = values.join('|');
        if (typeof product.priceMatrix[key] === 'number') {
          modifier += product.priceMatrix[key];
        }
      }
    }
    // 2. Add modifiers for non-matrix customizations (always) and matrix-less products.
    product.customizations.forEach((c) => {
      const partOfMatrix = product.priceMatrix && !c.excludeFromMatrix;
      if (partOfMatrix) return; // already represented in the matrix value above
      const selected = selectedCustomizations[c.name];
      if (selected === undefined || selected === null) return;
      if (c.priceModifiers && typeof c.priceModifiers[selected] === 'number') {
        modifier += c.priceModifiers[selected];
        return;
      }
      if (c.inputType === 'quantity' && c.priceTiers && typeof selected === 'number' && selected > 0) {
        const tiers = c.priceTiers;
        if (tiers[selected] !== undefined) {
          modifier += tiers[selected];
        } else {
          const keys = Object.keys(tiers).map(Number).sort((a, b) => a - b);
          const maxKey = keys[keys.length - 1];
          const perExtra = typeof c.tierExtra === 'number' ? c.tierExtra : (tiers[1] || 0);
          modifier += tiers[maxKey] + (selected - maxKey) * perExtra;
        }
        return;
      }
      if (c.inputType === 'quantity' && typeof c.pricePerUnit === 'number' && typeof selected === 'number') {
        modifier += c.pricePerUnit * selected;
      }
    });
    return modifier;
  };
  const effectivePrice = product ? product.price + computePriceModifier() : 0;
  const sale = useSale();
  const onSale = saleAppliesTo(sale, product);
  const saleEffective = salePrice(effectivePrice, sale, product);
  const priceQty = quantity && quantity > 0 ? quantity : 1;
  const displayTotal = effectivePrice * priceQty;
  const displaySaleTotal = saleEffective * priceQty;
  const hideQuantity = ['tenjam-shayz-lounge-chair-set', 'tenjam-moon-chair-set', 'tenjam-woosah-adirondack-chair', 'tenjam-mibster-chair-set', 'tenjam-laylo-in-pool-lounger', 'lounger-pillow-laylo-shayz'].includes(product?.slug);

  const handleAddToCart = () => {
    if (!product) return;
    // Validate selections - all mandatory
    if (product.colors && product.colors.length > 0 && !selectedColor) {
      setValidationError('Please select a color before adding to cart.');
      return;
    }
    if (product.customizations && product.customizations.length > 0) {
      const missing = product.customizations.find(c => {
        const val = selectedCustomizations[c.name];
        // For quantity-type inputs, val can be 0 (valid). Treat undefined/null as missing.
        if (c.inputType === 'quantity') return val === undefined || val === null;
        return !val;
      });
      if (missing) {
        setValidationError(`Please make a selection for "${missing.name}" before adding to cart.`);
        return;
      }
    }
    // Umbrella sleeve confirmation must be checked when a "With Umbrella Hole" variant is selected.
    if (requiresUmbrellaSleeve && !umbrellaSleeveConfirmed) {
      setValidationError('Please confirm that an umbrella sleeve is installed in the pool floor before adding to cart.');
      return;
    }
    setValidationError('');
    addToCart(product, quantity, selectedColor, selectedCustomizations, effectivePrice);
  };

  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      try {
        const productData = await getProduct(slug);
        setProduct(productData);
        // Start with NO color or customization selected — user must explicitly pick each option.
        // Exception: single-color products auto-select their only color (no picker shown).
        setSelectedColor(Array.isArray(productData.colors) && productData.colors.length === 1 ? productData.colors[0] : '');
        const heroVideo = (Array.isArray(productData.videos) && productData.videos[0]) || productData.video || null;
        setDisplayImage(heroVideo ? VIDEO_SLIDE : productData.image);
        setSelectedCustomizations({});
        setSlideIndex(0);
        setManualPick(false);

        const allProducts = await getProducts({ category: productData.category });
        const related = allProducts.filter(p => p.id !== productData.id).slice(0, 4);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [slug]);

  // Auto-advancing lifestyle slideshow shown before a color is selected.
  // Stops when the shopper picks a color or clicks a specific photo.
  useEffect(() => {
    if (!product) return;
    const colorSet = (selectedColor && product.colorExtraImages && Array.isArray(product.colorExtraImages[selectedColor]))
      ? [ (product.colorImages && product.colorImages[selectedColor]) || product.image, ...product.colorExtraImages[selectedColor] ]
      : null;
    const imgs = colorSet || ((Array.isArray(product.images) && product.images.length > 0) ? product.images : [product.image]);
    const isGallery = Array.isArray(product.galleryImages) && product.galleryImages.length > 0;
    const heroVideo = (Array.isArray(product.videos) && product.videos[0]) || product.video || null;
    if (manualPick || isGallery || heroVideo || imgs.length <= 1) return;
    const id = setInterval(() => {
      setSlideIndex((i) => {
        const next = (i + 1) % imgs.length;
        setDisplayImage(imgs[next]);
        return next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [product, selectedColor, manualPick]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <Link to="/products" className="text-cyan-600 hover:text-cyan-700 font-medium">
            ← Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = product.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  // Color grouping: standard (quick ship) vs custom colors. Frozin cold plunges are excluded.
  const allColors = product.colors || [];
  const standardColors = allColors.filter((c) => STANDARD_COLORS.includes(c));
  const customColors = allColors.filter((c) => !STANDARD_COLORS.includes(c));
  const useColorGroups = product.category !== 'frozin-400-products' && standardColors.length > 0 && customColors.length > 0;
  const colorBtnClass = (color) => `px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all duration-300 ${
    selectedColor === color
      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg scale-105'
      : 'bg-white text-gray-700 border border-gray-300 hover:border-cyan-500'
  }`;

  // Thumbnail gallery: the color-selected product image + any extra reference/diagram images.
  const hasGallery = Array.isArray(product.galleryImages) && product.galleryImages.length > 0;
  const colorImage = (product.colorImages && product.colorImages[selectedColor]) || product.image;
  // When a color with its own photo set is selected, the whole gallery (main + thumbnails)
  // switches to that color's images. Otherwise fall back to the generic lifestyle images.
  const colorImageSet = (selectedColor && product.colorExtraImages && Array.isArray(product.colorExtraImages[selectedColor]))
    ? [colorImage, ...product.colorExtraImages[selectedColor]]
    : null;
  const lifestyleImages = colorImageSet || ((Array.isArray(product.images) && product.images.length > 0) ? product.images : [product.image]);
  const hasSlideshow = !hasGallery && lifestyleImages.length > 1;
  // Product hero video shown as the FIRST slide in the gallery/slideshow.
  const heroVideo = (Array.isArray(product.videos) && product.videos[0]) || product.video || null;
  const imageThumbs = hasGallery ? [colorImage, ...product.galleryImages] : (hasSlideshow ? lifestyleImages : []);
  const showVideoSlide = !!heroVideo && (hasGallery || hasSlideshow);
  const thumbnails = showVideoSlide ? [VIDEO_SLIDE, ...imageThumbs] : imageThumbs;
  const showingVideoSlide = displayImage === VIDEO_SLIDE;
  const showingGalleryImage = hasGallery && product.galleryImages.includes(displayImage);

  // True when an option-specific reference image (e.g. riser/depth diagram) is currently shown
  // below the main image. In that case we shrink both so the customer can see them at once.
  const optionImageActive = !!(product.customizationOptionImages && product.customizations?.some((c) => {
    const oi = product.customizationOptionImages[c.name];
    const sel = selectedCustomizations[c.name];
    return oi && sel && oi[sel];
  }));

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 sm:pb-20">
      <SEO
        title={`${product.name} | In-Pool Furniture`}
        description={`${product.description.slice(0, 155)}...`}
        canonical={`https://rezvanifurniturestudio.com/products/${product.category}/${product.slug}`}
        type="product"
        image={product.image}
        price={salePrice(product.price, sale, product)}
        originalPrice={product.price}
        jsonLd={[
          // Wait for live sale data so the Offer price never reports a stale list price.
          ...(sale.loaded ? [productSchema(product, sale)] : []),
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: categoryName, url: `/products/${product.category}` },
            { name: product.name }
          ])
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-6 sm:mb-8">
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm">
            <li><Link to="/" className="text-gray-500 hover:text-cyan-600">Home</Link></li>
            <li className="text-gray-400">/</li>
            <li><Link to="/products" className="text-gray-500 hover:text-cyan-600">Products</Link></li>
            <li className="text-gray-400">/</li>
            <li><Link to={`/products?category=${product.category}`} className="text-gray-500 hover:text-cyan-600">{categoryName}</Link></li>
            <li className="text-gray-400">/</li>
            <li className="text-cyan-600 font-medium break-all">{product.name}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 mb-14 sm:mb-20">
          {/* Product Image */}
          <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div
              className={`${hasGallery ? 'max-w-sm mx-auto aspect-square' : optionImageActive ? 'max-w-xs mx-auto aspect-square' : product.customizationOptionImages ? 'max-w-md mx-auto aspect-[4/3]' : 'aspect-square'} rounded-3xl overflow-hidden shadow-2xl bg-white`}
              onTouchStart={thumbnails.length > 1 ? (e) => { touchStartX.current = e.changedTouches[0].clientX; } : undefined}
              onTouchEnd={thumbnails.length > 1 ? (e) => {
                if (touchStartX.current == null) return;
                const dx = e.changedTouches[0].clientX - touchStartX.current;
                if (Math.abs(dx) > 40) goToSlide(dx < 0 ? 1 : -1);
                touchStartX.current = null;
              } : undefined}
            >
              {showingVideoSlide ? (
                <video
                  src={imgUrl(heroVideo)}
                  poster={productImgUrl(product, colorImage, 800)}
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-contain bg-black"
                  data-testid="product-main-video"
                />
              ) : (
                <ZoomImage
                  src={productImgUrl(product, displayImage)}
                  alt={product.name}
                  className="w-full h-full"
                  imgClassName="transition-opacity duration-500"
                  objectFit="contain"
                  zoom={2.2}
                  testId="product-main-image"
                />
              )}
            </div>
            {(hasSlideshow || showVideoSlide) && thumbnails.length > 1 && (
              <div className="flex items-center justify-center gap-4" data-testid="slideshow-controls">
                <button
                  type="button"
                  aria-label="Previous image"
                  data-testid="slideshow-prev"
                  onClick={() => { const n = thumbnails.length; const ni = (slideIndex - 1 + n) % n; setSlideIndex(ni); setDisplayImage(thumbnails[ni]); }}
                  className="p-2 rounded-full bg-white border border-gray-200 shadow hover:bg-cyan-50 hover:border-cyan-300 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <span className="text-sm text-gray-500 tabular-nums min-w-[3rem] text-center">{(slideIndex % thumbnails.length) + 1} / {thumbnails.length}</span>
                <button
                  type="button"
                  aria-label="Next image"
                  data-testid="slideshow-next"
                  onClick={() => { const n = thumbnails.length; const ni = (slideIndex + 1) % n; setSlideIndex(ni); setDisplayImage(thumbnails[ni]); }}
                  className="p-2 rounded-full bg-white border border-gray-200 shadow hover:bg-cyan-50 hover:border-cyan-300 transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </div>
            )}

            {/* Thumbnail strip: lifestyle slideshow photos (before color pick) OR the
                color-selected image + reference photos. Shrinks as the count grows. */}
            {(hasGallery || hasSlideshow || showVideoSlide) && (() => {
              const n = thumbnails.length;
              const thumbSize =
                n <= 3 ? 'w-14 h-14 sm:w-16 sm:h-16'
                : n <= 5 ? 'w-12 h-12 sm:w-14 sm:h-14'
                : n <= 7 ? 'w-10 h-10 sm:w-12 sm:h-12'
                : 'w-9 h-9 sm:w-10 sm:h-10';
              return (
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3" data-testid="product-thumbnail-strip">
                {thumbnails.map((thumb, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setDisplayImage(thumb);
                      if (hasSlideshow || showVideoSlide) {
                        setManualPick(true);
                        setSlideIndex(idx);
                      }
                    }}
                    className={`${thumbSize} rounded-xl overflow-hidden border-2 bg-white transition-all ${displayImage === thumb ? 'border-cyan-500 ring-2 ring-cyan-200' : 'border-gray-200 hover:border-gray-400'}`}
                    aria-label={thumb === VIDEO_SLIDE ? 'Play product video' : (hasGallery ? (idx === 0 ? 'Selected color view' : `Reference image ${idx}`) : `${product.name} photo ${idx + 1}`)}
                    data-testid={`product-thumbnail-${idx}`}
                  >
                    {thumb === VIDEO_SLIDE ? (
                      <div className="relative w-full h-full bg-black">
                        <video
                          src={imgUrl(heroVideo)}
                          poster={productImgUrl(product, colorImage, 200)}
                          preload="metadata"
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                          <Play size={14} className="text-white ml-0.5" fill="currentColor" />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={productImgUrl(product, thumb, 200)}
                        alt={hasGallery ? (idx === 0 ? `${product.name} - selected color` : `${product.name} - reference ${idx}`) : `${product.name} - photo ${idx + 1}`}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    )}
                  </button>
                ))}
              </div>
              );
            })()}
            {/* Option-specific reference image(s) for current customization (e.g., depth/weight chart per riser size) - shown next to color options */}
            {product.customizationOptionImages && product.customizations && product.customizations.map((customization) => {
              const optImages = product.customizationOptionImages[customization.name];
              if (!optImages) return null;
              const selected = selectedCustomizations[customization.name];
              if (!selected || !optImages[selected]) return null;
              const list = Array.isArray(optImages[selected]) ? optImages[selected] : [optImages[selected]];
              return (
                <div key={`opt-img-${customization.name}`} className="space-y-3 max-w-xs mx-auto">
                  <p className="text-xs sm:text-sm font-semibold text-gray-700">
                    {customization.name}: <span className="text-cyan-600">{selected}</span>
                  </p>
                  {list.map((chartUrl, idx) => (
                    <div key={idx} className="rounded-xl overflow-hidden border border-gray-200 bg-white">
                      <ZoomImage
                        src={chartUrl}
                        alt={`${customization.name} - ${selected} reference chart ${idx + 1}`}
                        className="w-full h-52 sm:h-60"
                        objectFit="contain"
                        zoom={2}
                        testId={`customization-option-image-${customization.name.toLowerCase().replace(/\s+/g, '-')}-${selected.toLowerCase().replace(/\s+/g, '-')}-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              );
            })}
            {/* Extra lifestyle images for the currently selected color are shown as
                switchable thumbnails above; no separate grid needed. */}
            {/* Secondary customization image (e.g., water depth diagram) */}
            {product.customizationImages && (() => {
              const custValues = Object.values(selectedCustomizations);
              const matchedImage = custValues.reduce((found, val) => found || product.customizationImages[val], null);
              return matchedImage ? (
                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                  <ZoomImage
                    src={matchedImage}
                    alt="Product specifications"
                    natural
                    objectFit="contain"
                    zoom={2}
                  />
                </div>
              ) : null;
            })()}
            {/* Static spec/diagram image */}
            {product.specImage && (
              <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                <ZoomImage
                  src={product.specImage}
                  alt="Product specifications"
                  natural
                  objectFit="contain"
                  zoom={2}
                />
              </div>
            )}
            {/* Additional spec / detail images (shown for every color) */}
            {product.specImages && product.specImages.map((img, idx) => (
              <div key={idx} className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                <ZoomImage
                  src={img}
                  alt={`Product detail ${idx + 1}`}
                  loading="lazy"
                  natural
                  objectFit="contain"
                  zoom={2}
                />
              </div>
            ))}
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            {/* Category Badge */}
            <div className="mb-4">
              <span className="text-sm font-medium text-cyan-600 bg-cyan-50 px-4 py-2 rounded-full">
                {product.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </span>
            </div>

            {/* Product Name */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 break-words">{product.name}</h1>

            {/* Rating (combined Amazon + verified site reviews) */}
            {(() => {
              const amzCount = Number(product.reviews) || 0;
              const siteCount = reviewStats?.count || 0;
              const siteSum = reviewStats?.sum || 0;
              const total = amzCount + siteCount;
              const avg = total > 0
                ? Math.round((((Number(product.rating) || 0) * amzCount + siteSum) / total) * 10) / 10
                : Number(product.rating) || 0;
              return (
                <div className="flex items-center space-x-3 mb-5 sm:mb-6">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={18}
                        className={i < Math.floor(avg) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                  <span className="text-sm sm:text-base text-gray-600" data-testid="header-rating">
                    {avg} ({total} review{total === 1 ? '' : 's'})
                  </span>
                </div>
              );
            })()}

            {/* Price */}
            <div className="mb-5 sm:mb-6">
              {onSale ? (
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-4xl sm:text-5xl font-bold text-cyan-600" data-testid="product-price">
                    ${displaySaleTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-xl sm:text-2xl text-gray-400 line-through">
                    ${displayTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-xs font-bold text-white bg-cyan-600 rounded-full px-2.5 py-1" data-testid="sale-badge">
                    {salePercent(sale, product)}% OFF
                  </span>
                </div>
              ) : (
                <span className="text-4xl sm:text-5xl font-bold text-gray-900" data-testid="product-price">
                  ${displayTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
              {priceQty > 1 && (
                <span className="mt-1 block text-sm sm:text-base text-gray-500" data-testid="price-qty-note">
                  {priceQty} × ${effectivePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} each
                </span>
              )}
              {priceQty === 1 && effectivePrice !== product.price && (
                <span className="mt-1 block text-sm sm:text-base text-gray-500">
                  base ${product.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {' + '}
                  ${(effectivePrice - product.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} options
                </span>
              )}
            </div>

            {/* Frozin 400 spec strip */}
            {product.category === 'frozin-400-products' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="frozin-spec-strip">
                {[
                  { icon: Droplet, label: '400 qt Capacity' },
                  { icon: Ruler, label: 'Fits 6\'3" & 300 lb' },
                  { icon: Flag, label: 'Made in USA' },
                  { icon: Snowflake, label: product.slug === 'frozin-complete-cold-plunge-system' ? 'Chiller Included' : 'Chiller Ready' },
                ].map((spec, i) => {
                  const Icon = spec.icon;
                  return (
                    <div key={i} className="flex flex-col items-center text-center gap-1.5 rounded-2xl border border-cyan-100 bg-cyan-50/60 px-3 py-3" data-testid={`frozin-spec-${i}`}>
                      <Icon className="w-6 h-6 text-cyan-600" />
                      <span className="text-xs font-semibold text-gray-700 leading-tight">{spec.label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Frozin.com cross-sell: chiller & filtration system sold on frozin.com */}
            {product.category === 'frozin-400-products' && (
              <a
                href="https://frozin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="frozin-shop-link"
                className="group flex items-center gap-3 mb-6 rounded-2xl border border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50 px-4 py-3.5 transition-all hover:border-cyan-400 hover:shadow-md"
              >
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-cyan-600 text-white">
                  <Snowflake className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">Need a chiller or filtration system?</p>
                  <p className="text-xs text-gray-600">Shop the full Frozin lineup at frozin.com</p>
                </div>
                <ExternalLink className="h-4 w-4 flex-shrink-0 text-cyan-600 transition-transform group-hover:translate-x-0.5" />
              </a>
            )}

            {/* Sale Countdown (only within the final-week window for this product's sale) */}
            {onSale && <SaleCountdown className="mb-6" item={product} />}

            {/* Description */}
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">{product.description}</p>

            {/* Color Selection */}
            {product.colors && product.colors.length > 1 && (
            <div className="mb-5 sm:mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Select Color: {selectedColor ? (
                  <span className="text-cyan-600">{selectedColor}</span>
                ) : (
                  <span className="text-red-500 font-medium">Required</span>
                )}
              </label>
              {useColorGroups ? (
                <div className="space-y-4" data-testid="color-swatches">
                  <div data-testid="standard-colors-group">
                    <p className="text-sm font-medium text-gray-800">Standard Colors</p>
                    <p className="text-xs text-gray-500 mb-2">Ships in 1-7 days</p>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {standardColors.map((color) => (
                        <button
                          key={color}
                          onClick={() => handleColorSelect(color)}
                          className={colorBtnClass(color)}
                          data-testid={`color-option-${color.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div data-testid="custom-colors-group">
                    <p className="text-sm font-medium text-gray-800">Custom Colors</p>
                    <p className="text-xs text-gray-500 mb-2">Ships in 1-4 weeks</p>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {customColors.map((color) => (
                        <button
                          key={color}
                          onClick={() => handleColorSelect(color)}
                          className={colorBtnClass(color)}
                          data-testid={`color-option-${color.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 sm:gap-3" data-testid="color-swatches">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorSelect(color)}
                      className={colorBtnClass(color)}
                      data-testid={`color-option-${color.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Customizations */}
            {product.customizations && product.customizations.length > 0 && (
              product.customizations.map((customization) => (
                <div key={customization.name} className="mb-5 sm:mb-6">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <label className="block text-sm font-semibold text-gray-900">
                      {customization.name}: {(selectedCustomizations[customization.name] !== undefined && selectedCustomizations[customization.name] !== null && selectedCustomizations[customization.name] !== '') ? (
                        <span className="text-cyan-600">
                          {customization.inputType === 'quantity'
                            ? `${selectedCustomizations[customization.name]} ${customization.unit || ''}${selectedCustomizations[customization.name] === 1 ? '' : 's'}`
                            : selectedCustomizations[customization.name]}
                        </span>
                      ) : (
                        <span className="text-red-500 font-medium">Required</span>
                      )}
                    </label>
                    {/* Info icons for known info topics */}
                    {product.customizationInfo && Object.keys(product.customizationInfo).map((topic) =>
                      (customization.name.toLowerCase().includes(topic.toLowerCase()) ||
                       (customization.options && customization.options.some((o) => o.toLowerCase().includes(topic.toLowerCase())))) ? (
                        <button
                          key={topic}
                          type="button"
                          onClick={() => setActiveInfo({ title: topic, ...product.customizationInfo[topic] })}
                          className="inline-flex items-center gap-1 text-xs font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 px-2 py-1 rounded-full transition-colors duration-200"
                          data-testid={`info-icon-${topic.toLowerCase().replace(/\s+/g, '-')}`}
                          aria-label={`More info about ${topic}`}
                        >
                          <Info size={13} />
                          <span>What is {topic}?</span>
                        </button>
                      ) : null
                    )}
                  </div>
                  {customization.inputType === 'quantity' ? (
                    <div className="flex items-center gap-3" data-testid={`quantity-${customization.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <button
                        type="button"
                        onClick={() => {
                          const current = selectedCustomizations[customization.name];
                          const min = customization.min ?? 0;
                          const next = current === undefined || current === null ? min : Math.max(min, current - 1);
                          handleCustomizationSelect(customization.name, next);
                        }}
                        className="w-11 h-11 rounded-xl bg-white border border-gray-300 hover:border-cyan-500 font-semibold text-lg transition-all"
                        data-testid={`quantity-${customization.name.toLowerCase().replace(/\s+/g, '-')}-minus`}
                        aria-label="Decrease"
                      >
                        −
                      </button>
                      <div
                        className={`min-w-[3.5rem] px-4 py-2.5 rounded-xl text-center text-lg font-semibold border-2 ${
                          selectedCustomizations[customization.name] !== undefined && selectedCustomizations[customization.name] !== null
                            ? 'border-cyan-500 bg-cyan-50 text-gray-900'
                            : 'border-dashed border-red-300 bg-red-50 text-red-500'
                        }`}
                        data-testid={`quantity-${customization.name.toLowerCase().replace(/\s+/g, '-')}-value`}
                      >
                        {selectedCustomizations[customization.name] !== undefined && selectedCustomizations[customization.name] !== null
                          ? selectedCustomizations[customization.name]
                          : '—'}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const current = selectedCustomizations[customization.name];
                          const max = customization.max ?? 99;
                          const min = customization.min ?? 0;
                          const next = current === undefined || current === null ? Math.max(min, 1) : Math.min(max, current + 1);
                          handleCustomizationSelect(customization.name, next);
                        }}
                        className="w-11 h-11 rounded-xl bg-white border border-gray-300 hover:border-cyan-500 font-semibold text-lg transition-all"
                        data-testid={`quantity-${customization.name.toLowerCase().replace(/\s+/g, '-')}-plus`}
                        aria-label="Increase"
                      >
                        +
                      </button>
                      <span className="text-xs sm:text-sm text-gray-500">
                        {customization.min ?? 0}–{customization.max ?? 99} {customization.unit || ''}{(customization.max ?? 99) > 1 ? 's' : ''}
                        {(() => {
                          const sel = selectedCustomizations[customization.name];
                          if (typeof sel !== 'number' || sel <= 0) return null;
                          let add = 0;
                          if (customization.priceTiers) {
                            const t = customization.priceTiers;
                            if (t[sel] !== undefined) {
                              add = t[sel];
                            } else {
                              const ks = Object.keys(t).map(Number).sort((a, b) => a - b);
                              const mk = ks[ks.length - 1];
                              const pe = typeof customization.tierExtra === 'number' ? customization.tierExtra : (t[1] || 0);
                              add = t[mk] + (sel - mk) * pe;
                            }
                          } else if (typeof customization.pricePerUnit === 'number') {
                            add = customization.pricePerUnit * sel;
                          }
                          return add > 0 ? <span className="ml-2 text-cyan-600 font-semibold" data-testid={`quantity-${customization.name.toLowerCase().replace(/\s+/g, '-')}-price`}>+${add.toFixed(2)}</span> : null;
                        })()}
                      </span>
                    </div>
                  ) : (
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {customization.options.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleCustomizationSelect(customization.name, option)}
                        className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all duration-300 ${
                          selectedCustomizations[customization.name] === option
                            ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg scale-105'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-cyan-500'
                        }`}
                        data-testid={`custom-${customization.name.toLowerCase().replace(/\s+/g, '-')}-${option.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {option}
                        {customization.priceModifiers && customization.priceModifiers[option] > 0 && (
                          <span className="ml-1.5 text-xs opacity-80">+${customization.priceModifiers[option].toFixed(2)}</span>
                        )}
                      </button>
                    ))}
                  </div>
                  )}
                  {/* Option-specific chart images are rendered in the left column (below the main product image) */}
                </div>
              ))
            )}

            {product.depthGuide && (
              <DepthGuide guide={product.depthGuide} selectedCustomizations={selectedCustomizations} />
            )}

            {/* Quantity */}
            <div className="mb-8">
              {/* Water Depth Configurator - Laylo only */}
              {product.slug && (product.slug.includes('laylo') || product.slug.includes('shayz')) && (
                <a
                  href={product.slug.includes('laylo') ? "https://laylo-configurator.vercel.app/" : "https://shayz-configurator.vercel.app/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="water-depth-configurator-btn"
                  className="mb-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                >
                  <Waves size={20} />
                  Water Depth Product Configurator
                </a>
              )}
              {!hideQuantity && (
                <>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Quantity
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      data-testid="quantity-decrease"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-12 h-12 rounded-xl bg-white border border-gray-300 hover:border-cyan-500 font-semibold transition-all"
                    >
                      −
                    </button>
                    <span data-testid="quantity-value" className="text-2xl font-bold text-gray-900 w-16 text-center">{quantity}</span>
                    <button
                      data-testid="quantity-increase"
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-12 h-12 rounded-xl bg-white border border-gray-300 hover:border-cyan-500 font-semibold transition-all"
                    >
                      +
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Umbrella sleeve confirmation — appears only when a "With Umbrella Hole" variant is selected */}
            {requiresUmbrellaSleeve && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="umbrella-sleeve-block">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={umbrellaSleeveConfirmed}
                    onChange={(e) => {
                      setUmbrellaSleeveConfirmed(e.target.checked);
                      if (e.target.checked) setValidationError('');
                    }}
                    className="mt-1 h-5 w-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                    data-testid="umbrella-sleeve-checkbox"
                  />
                  <span className="text-sm text-amber-900">
                    <span className="font-semibold">Required:</span> I confirm that an <strong>umbrella sleeve is installed</strong> in the pool floor to receive the umbrella for this product.
                  </span>
                </label>
              </div>
            )}

            {/* Validation error */}
            {validationError && (
              <div
                className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                role="alert"
                data-testid="validation-error"
              >
                {validationError}
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex gap-3 sm:gap-4 mb-6 sm:mb-8">
              <button
                onClick={handleAddToCart}
                data-testid="add-to-cart-btn"
                className="flex-1 px-5 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-base sm:text-lg font-semibold rounded-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <ShoppingCart size={20} className="sm:w-[22px] sm:h-[22px]" />
                <span>Add to Cart</span>
              </button>
              <button className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 rounded-xl border-2 border-gray-300 hover:border-red-500 hover:bg-red-50 flex items-center justify-center transition-all duration-300 group">
                <Heart size={22} className="text-gray-400 group-hover:text-red-500 group-hover:fill-red-500 transition-all sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Features */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-5 sm:p-6 mb-6 sm:mb-8">
              <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Key Features</h3>
              <ul className="space-y-2 sm:space-y-3">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-white" />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                  </li>
                ))}
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-white" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700">Water Depth Support: {product.waterDepth}</span>
                </li>
              </ul>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Truck size={20} className="text-cyan-600 sm:w-6 sm:h-6" />
                </div>
                <p className="text-[11px] sm:text-xs text-gray-600 font-medium">Free Shipping</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Shield size={20} className="text-cyan-600 sm:w-6 sm:h-6" />
                </div>
                <p className="text-[11px] sm:text-xs text-gray-600 font-medium">Warranty Included</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <RotateCcw size={20} className="text-cyan-600 sm:w-6 sm:h-6" />
                </div>
                <p className="text-[11px] sm:text-xs text-gray-600 font-medium">Easy Returns</p>
              </div>
            </div>
          </div>
        </div>

        {/* See More - Detailed Description */}
        {product.detailedDescription && (
          <div className="mb-20">
            <button
              onClick={() => setShowDetails(!showDetails)}
              data-testid="see-more-btn"
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-gray-200 hover:border-cyan-500 rounded-2xl font-semibold text-gray-900 hover:text-cyan-600 transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <span>{showDetails ? 'See Less' : 'See More Details'}</span>
              {showDetails ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {showDetails && (
              <div className="mt-6 bg-white rounded-2xl shadow-lg p-8 lg:p-12 prose prose-lg max-w-none">
                {product.detailedDescription.split('\n\n').map((section, idx) => {
                  const lines = section.split('\n');
                  const firstLine = lines[0];
                  const isHeading = firstLine.endsWith(':') || firstLine.startsWith('#');
                  return (
                    <div key={idx} className="mb-6">
                      {isHeading ? (
                        <>
                          <h3 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-100 pb-2">
                            {firstLine.replace(/^#+\s*/, '').replace(/:$/, '')}
                          </h3>
                          {lines.slice(1).map((line, lineIdx) => (
                            <p key={lineIdx} className="text-gray-700 leading-relaxed mb-2">{line}</p>
                          ))}
                        </>
                      ) : (
                        lines.map((line, lineIdx) => (
                          <p key={lineIdx} className="text-gray-700 leading-relaxed mb-2">{line}</p>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Product Videos Gallery — shown for products with videos[] (excludes the hero video already shown as slide 1) */}
        {(() => {
          const sectionVideos = (product.videos || []).filter((v) => !(showVideoSlide && v === heroVideo));
          if (sectionVideos.length === 0) return null;
          return (
          <div className="mb-14 sm:mb-20">
            <div className="flex items-center justify-between mb-5 sm:mb-6 flex-wrap gap-2">
              <div>
                <span className="text-xs sm:text-sm font-semibold text-cyan-600 bg-cyan-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full uppercase tracking-wider">
                  Watch &amp; Discover
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-3 sm:mt-4">Product Videos</h2>
              </div>
              <p className="text-sm text-gray-600 hidden sm:block">Click any video to play it right here on the page.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="product-videos-grid">
              {sectionVideos.map((src, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveVideo(src)}
                  data-testid={`product-video-${idx}`}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 text-left"
                >
                  <div className="relative aspect-video overflow-hidden bg-gray-900">
                    <video
                      src={imgUrl(src)}
                      preload="metadata"
                      muted
                      playsInline
                      className="w-full h-full object-contain bg-black transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent flex items-center justify-center transition-all duration-300 group-hover:from-black/60">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transform transition-transform duration-300 group-hover:scale-110">
                        <Play size={28} className="text-cyan-600 ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          );
        })()}

        {/* What's Included — shown by the product video */}
        {Array.isArray(product.whatsIncluded) && product.whatsIncluded.length > 0 && (
          <div className="mb-16 sm:mb-20" data-testid="whats-included">
            <div className="text-center mb-8">
              <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wide">In The Box</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-3 sm:mt-4">What's Included</h2>
            </div>
            <ul className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-3">
              {product.whatsIncluded.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <Check className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}


        {/* More Information — Ballast Stabilization System (selected products) */}
        {product.slug && BALLAST_PRODUCT_NAMES[product.slug] && (() => {
          const branded = BALLAST_PRODUCT_NAMES[product.slug].display;
          return (
            <div className="mb-20">
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-slate-900 to-cyan-950 text-white p-8 sm:p-10 lg:p-12 shadow-2xl" data-testid="ballast-more-info">
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                  <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 600" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 300C240 200 480 400 720 300C960 200 1200 400 1440 300V600H0V300Z" fill="url(#pdWaveGrad)" />
                    <defs>
                      <linearGradient id="pdWaveGrad" x1="0" y1="0" x2="1440" y2="600" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#22d3ee" />
                        <stop offset="1" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <div className="relative">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-400/30 backdrop-blur-sm mb-5">
                    <Sparkles size={16} className="text-cyan-300" />
                    <span className="text-xs font-semibold text-cyan-300 uppercase tracking-[0.2em]">More Information</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight">
                    Patent-Pending <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400">Ballast Stabilization System</span>
                  </h2>

                  <div className="space-y-5 text-base sm:text-lg text-gray-300 leading-relaxed max-w-4xl">
                    <p>
                      Tenjam&rsquo;s Shayz&reg; Lounger, Tenjam&rsquo;s Laylo&reg; Lounger, Freelo Chair&reg;, Woosah Chair&reg;, Moon Chair&reg;, and Mibster Chair&reg; are all engineered with a <strong className="text-white">patent-pending Ballast Stabilization System</strong> that delivers unmatched in-water performance. Instead of relying on outdated fill-and-drain methods, Tenjam&rsquo;s integrated ballast technology automatically locks the lounger into place on your tanning ledge or Baja shelf&mdash;no sand, no water filling, no hassle.
                    </p>
                    <p>
                      This advanced design also eliminates the hidden problem many loungers face: internal algae growth. Because the {branded} contains no hollow cavities, no fill ports, and no standing water, algae has nowhere to grow. Your lounger stays clean, sanitary, and maintenance-free season after season.
                    </p>
                    <p>
                      Crafted from Tenjam&rsquo;s commercial-grade, UV-stable, antimicrobial material, the {branded} is built to outperform and outlast traditional in-pool loungers&mdash;delivering a cleaner, safer, and more reliable experience for residential and commercial pools.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                    {[
                      { icon: Anchor, title: 'Anchored & Stable' },
                      { icon: Shield, title: 'Algae-Free Design' },
                      { icon: Waves, title: 'No Fill Required' },
                      { icon: Sparkles, title: 'Cleaner. Safer.' },
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                          <f.icon size={18} className="text-white" />
                        </div>
                        <span className="text-sm font-semibold text-white">{f.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        <ProductVideos product={product} />

        {/* Customer Reviews (verified-buyer reviews + Amazon reviews) */}
        <ProductReviews product={product} onStats={setReviewStats} />

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Customization Info Modal */}
      {activeInfo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-8"
          onClick={() => setActiveInfo(null)}
          data-testid="customization-info-modal"
        >
          <button
            onClick={() => setActiveInfo(null)}
            aria-label="Close info"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-all duration-300 z-10"
          >
            <X size={22} />
          </button>
          <div
            className="relative w-full max-w-3xl bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 mb-3 sm:mb-4">
                <Info size={18} className="text-cyan-600" />
                <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-cyan-600">More Information</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-5">{activeInfo.title}</h3>
              {activeInfo.image && (
                <div className="rounded-xl overflow-hidden border border-gray-200 mb-5 sm:mb-6">
                  <img
                    src={imgUrl(activeInfo.image)}
                    alt={activeInfo.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto object-contain bg-gray-50"
                  />
                </div>
              )}
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                {activeInfo.text}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal Player */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-3 sm:p-8"
          onClick={() => setActiveVideo(null)}
          data-testid="pd-video-modal"
        >
          <button
            onClick={() => setActiveVideo(null)}
            aria-label="Close video"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-all duration-300 z-10"
          >
            <X size={22} />
          </button>
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video w-full bg-black rounded-2xl overflow-hidden shadow-2xl">
              <video
                src={imgUrl(activeVideo)}
                autoPlay
                controls
                playsInline
                className="w-full h-full"
                data-testid="pd-video-modal-player"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
